import { configureStore } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import { persistStore, persistReducer, REHYDRATE, FLUSH, PAUSE, PERSIST, PURGE, REGISTER, } from 'redux-persist';
import AsyncStorage from '@react-native-async-storage/async-storage';
import rootReducer from './reducers';
import { abbiWayApi } from '../services/abbiWayApi';
import { setEmail, setEnvironmentConfig, setForcedSignoutVersion, signout, } from './appSlice';
import { FORCED_SIGNOUT_VERSION, resolveForcedSignout } from './forcedSignout';
import { webAppEnvStripTransform } from './webPersistTransform';
import { signoutResetMiddleware } from './signoutResetMiddleware';
import { resolveDefaultEnvironmentConfig, resolveStoreBuildRehydrate, sanitizeSavedSigninEmail, } from '../config/environment';
const persistConfig = {
    key: 'root',
    version: 2,
    storage: AsyncStorage,
    /** Top-level reducer keys only (`whiteList` was invalid and effectively persisted everything). */
    whitelist: ['app'],
    transforms: [webAppEnvStripTransform],
    migrate: (state, _currentVersion) => {
        if (!state || typeof state !== 'object') {
            return Promise.resolve(state);
        }
        const next = { ...state };
        delete next.abbiWayApi;
        return Promise.resolve(next);
    },
};
const persistedReducer = persistReducer(persistConfig, rootReducer);
// https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
export const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware => getDefaultMiddleware({
        serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
    }).concat(abbiWayApi.middleware, signoutResetMiddleware),
});
/**
 * On localhost, align linking prefix with the actual browser origin (port, 127.0.0.1 vs localhost).
 * Keeps backend/environment as rehydrated so API targets stay stable.
 */
function normalizeLocalWebFrontendOrigin() {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return;
    }
    const { hostname, origin } = window.location;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        return;
    }
    const state = store.getState();
    const fe = state.app?.frontendBaseUrl ?? '';
    const stripTrailingSlash = (u) => u.replace(/\/$/, '');
    if (stripTrailingSlash(fe) === stripTrailingSlash(origin)) {
        return;
    }
    store.dispatch(setEnvironmentConfig({
        environment: state.app.environment,
        backendBaseUrl: state.app.backendBaseUrl,
        frontendBaseUrl: origin,
    }));
}
export const persistor = persistStore(store, null, () => {
    let forced = false;
    const savedEmail = store.getState().app.email;
    const cleanEmail = sanitizeSavedSigninEmail(savedEmail);
    if (cleanEmail !== savedEmail) {
        // A test$ prefix that no longer reaches TEST: save and prefill the plain email.
        store.dispatch(setEmail(cleanEmail));
    }
    const { signOut, markVersion } = resolveForcedSignout(store.getState().app);
    if (signOut) {
        // One-time sign-out after an upgrade (native and web); back to the default
        // server (prod in store builds and on app.abbisway.com).
        store.dispatch(signout());
        store.dispatch(setEnvironmentConfig(resolveDefaultEnvironmentConfig()));
        forced = true;
    }
    if (markVersion) {
        store.dispatch(setForcedSignoutVersion(FORCED_SIGNOUT_VERSION));
    }
    if (__STORE_BUILD__ && !forced) {
        const app = store.getState().app;
        const { network, clearSession } = resolveStoreBuildRehydrate({
            email: app?.email,
            backendBaseUrl: app?.backendBaseUrl,
        });
        if (clearSession) {
            // Persisted session belongs to another backend (e.g. TEST); sign in again.
            store.dispatch(signout());
        }
        store.dispatch(setEnvironmentConfig(network));
    }
    normalizeLocalWebFrontendOrigin();
});
